import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, Link, useLocation } from 'react-router-dom';
import { TRADES } from '@/lib/calculators';
import api, { formatApiError } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { EST } from '@/constants/testIds';
import { Plus, Trash2, Save, Printer, User2, Settings as SettingsIcon, Eye, X, Download, Briefcase } from 'lucide-react';
import { toast, Toaster } from 'sonner';
import PrintableInvoice from '@/components/PrintableInvoice';
import ShareSection from '@/components/ShareSection';
import PhotoAttachments from '@/components/PhotoAttachments';
import QuoteTemplateBar from '@/components/QuoteTemplateBar';
import Seo from '@/components/Seo';

const blankItem = () => ({ description: '', qty: 1, unit_price: 0, unit: 'item' });

const STATUS_OPTIONS = [
  { value: 'draft',    label: 'Draft',    color: 'bg-slate-200 text-slate-900' },
  { value: 'sent',     label: 'Sent',     color: 'bg-blue-200 text-blue-900' },
  { value: 'accepted', label: 'Accepted', color: 'bg-emerald-200 text-emerald-900' },
  { value: 'declined', label: 'Declined', color: 'bg-rose-200 text-rose-900' },
  { value: 'paid',     label: 'Paid',     color: 'bg-brand text-white' },
];

export default function EstimatorPage() {
  const { id } = useParams();
  const { user, refresh } = useAuth();
  const nav = useNavigate();

  const [name, setName] = useState('');
  const [quoteNumber, setQuoteNumber] = useState('');
  const [trade, setTrade] = useState('general-builders');
  const { state: navState } = useLocation();
  const pre = navState?.customer;
  // Prefilled when started from a customer record
  const [customer, setCustomer] = useState({ name: pre?.name || '', address: pre?.address || '', phone: pre?.phone || '', email: pre?.email || '' });
  const [status, setStatus] = useState('draft');
  const [logoUrl, setLogoUrl] = useState(null);
  const [savedProject, setSavedProject] = useState(null); // includes share fields
  const [items, setItems] = useState([blankItem()]);
  const [labourHours, setLabourHours] = useState(0);
  const [labourRate, setLabourRate] = useState(35);
  const [labourDays, setLabourDays] = useState(0);
  const [labourDayRate, setLabourDayRate] = useState(180);
  const [extraMaterials, setExtraMaterials] = useState(0);
  const [vat, setVat] = useState(20);
  const [margin, setMargin] = useState(15);
  const [overheadsPerDay, setOverheadsPerDay] = useState(0);
  const [notes, setNotes] = useState('');
  const [showMarginOnPdf, setShowMarginOnPdf] = useState(false);
  const [pdfTemplate, setPdfTemplate] = useState('detailed'); // 'detailed' | 'simple'
  const [paymentTerms, setPaymentTerms] = useState('Payment terms: 14 days from invoice unless otherwise agreed.');
  const [terms, setTerms] = useState('');
  const [quoteValidityDays, setQuoteValidityDays] = useState(30);
  const [busy, setBusy] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [creatingJob, setCreatingJob] = useState(false);
  const pdfRef = useRef(null);
  const [autosaveState, setAutosaveState] = useState('idle'); // 'idle' | 'saving' | 'saved' | 'error' | 'offline'
  const [initialLoaded, setInitialLoaded] = useState(false);
  const [restoreOffer, setRestoreOffer] = useState(null); // null | { draft, ts }
  const draftKey = `tm-quote-draft-${id || 'new'}`;

  useEffect(() => {
    // Fetch logo if user has one (for invoice print)
    if (user?.has_logo) {
      api.get('/me/logo').then(({ data }) => setLogoUrl(data.data_url)).catch(() => {});
    }
  }, [user]);

  useEffect(() => {
    if (!id) {
      // New quote: auto-fetch next quote number for logged-in users
      if (user?.is_pro || user?.role === 'admin') {
        api.get('/projects/next-number')
          .then(({ data }) => setQuoteNumber((qn) => qn || data.quote_number))
          .catch(() => {});
      }
      return;
    }
    api.get(`/projects/${id}`).then(({ data }) => {
      setSavedProject(data);
      setName(data.name); setTrade(data.trade);
      setQuoteNumber(data.quote_number || '');
      setCustomer({
        name: data.customer_name || '',
        address: data.customer_address || '',
        phone: data.customer_phone || '',
        email: data.customer_email || '',
      });
      setStatus(data.status || 'draft');
      const loadedItems = data.items?.length ? data.items : [blankItem()];
      setItems(loadedItems);
      setLabourHours(data.labour_hours); setLabourRate(data.labour_rate);
      setLabourDays(data.labour_days || 0); setLabourDayRate(data.labour_day_rate || 180);
      // materials_cost stored on the project is the SUM of (line items + extras).
      // Recover the extras-only portion so the "Additional materials" field is correct.
      const loadedItemsTotal = loadedItems.reduce(
        (s, it) => s + (parseFloat(it.qty) || 0) * (parseFloat(it.unit_price) || 0),
        0,
      );
      const extrasOnly = Math.max(0, (parseFloat(data.materials_cost) || 0) - loadedItemsTotal);
      setExtraMaterials(extrasOnly);
      setVat(data.vat_rate);
      setMargin(data.margin_pct); setNotes(data.notes || '');
      if (data.overhead_per_day != null) setOverheadsPerDay(data.overhead_per_day);
      setPdfTemplate(data.pdf_template || 'detailed');
      if (data.payment_terms != null) setPaymentTerms(data.payment_terms);
      if (data.terms != null) setTerms(data.terms);
      if (data.quote_validity_days != null) setQuoteValidityDays(data.quote_validity_days);
      // Refresh auth context so business info / logo are current on the PDF
      if (refresh) refresh();
      setInitialLoaded(true);
    }).catch(() => toast.error('Could not load quote'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Mark new (no-id) quotes as "ready" so autosave kicks in
  useEffect(() => {
    if (!id) setInitialLoaded(true);
  }, [id]);

  // Pre-fill a brand-new quote with the business default Terms & Conditions
  useEffect(() => {
    if (!id && user?.default_terms && !terms) setTerms(user.default_terms);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, user]);

  // Bundle the editable form state — used for both localStorage and cloud autosave
  const formState = useMemo(() => ({
    name, quoteNumber, trade, customer, status, items,
    labourHours, labourRate, labourDays, labourDayRate, extraMaterials,
    vat, margin, overheadsPerDay, notes, pdfTemplate, paymentTerms, quoteValidityDays,
    terms,
    showMarginOnPdf,
  }), [name, quoteNumber, trade, customer, status, items, labourHours,
       labourRate, labourDays, labourDayRate, extraMaterials, vat, margin,
       overheadsPerDay, notes, pdfTemplate, paymentTerms, quoteValidityDays, terms, showMarginOnPdf]);

  // On mount: detect a stale localStorage draft and offer to restore.
  // Done in its own one-shot effect that fires once initialLoaded becomes true.
  const [restoreChecked, setRestoreChecked] = useState(false);
  useEffect(() => {
    if (!initialLoaded || restoreChecked) return;
    setRestoreChecked(true);
    try {
      const raw = localStorage.getItem(draftKey);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (!parsed || !parsed._ts) { localStorage.removeItem(draftKey); return; }
      // Don't offer a restore if the saved draft already matches what we just loaded
      const same = JSON.stringify({ ...parsed, _ts: undefined }) === JSON.stringify({ ...formState, _ts: undefined });
      if (same) return;
      // Only offer restores newer than 7 days
      if (Date.now() - parsed._ts > 7 * 24 * 3600 * 1000) {
        localStorage.removeItem(draftKey);
        return;
      }
      setRestoreOffer({ draft: parsed, ts: parsed._ts });
    } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialLoaded]);

  const applyRestore = () => {
    if (!restoreOffer) return;
    const d = restoreOffer.draft;
    if (d.name != null) setName(d.name);
    if (d.quoteNumber != null) setQuoteNumber(d.quoteNumber);
    if (d.trade) setTrade(d.trade);
    if (d.customer) setCustomer(d.customer);
    if (d.status) setStatus(d.status);
    if (Array.isArray(d.items) && d.items.length) setItems(d.items);
    if (d.labourHours != null) setLabourHours(d.labourHours);
    if (d.labourRate != null) setLabourRate(d.labourRate);
    if (d.labourDays != null) setLabourDays(d.labourDays);
    if (d.labourDayRate != null) setLabourDayRate(d.labourDayRate);
    if (d.extraMaterials != null) setExtraMaterials(d.extraMaterials);
    if (d.vat != null) setVat(d.vat);
    if (d.margin != null) setMargin(d.margin);
    if (d.overheadsPerDay != null) setOverheadsPerDay(d.overheadsPerDay);
    if (d.notes != null) setNotes(d.notes);
    if (d.pdfTemplate) setPdfTemplate(d.pdfTemplate);
    if (d.paymentTerms != null) setPaymentTerms(d.paymentTerms);
    if (d.terms != null) setTerms(d.terms);
    if (d.quoteValidityDays != null) setQuoteValidityDays(d.quoteValidityDays);
    if (d.showMarginOnPdf != null) setShowMarginOnPdf(d.showMarginOnPdf);
    toast.success('Unsaved changes restored');
    setRestoreOffer(null);
  };

  const dismissRestore = () => {
    try { localStorage.removeItem(draftKey); } catch { /* ignore */ }
    setRestoreOffer(null);
  };

  // Layer 2: localStorage autosave on every change (instant, no debounce)
  useEffect(() => {
    if (!initialLoaded || restoreOffer) return;
    try {
      localStorage.setItem(draftKey, JSON.stringify({ ...formState, _ts: Date.now() }));
    } catch { /* localStorage full or disabled — silent */ }
  }, [formState, initialLoaded, draftKey, restoreOffer]);

  // Layer 3: debounced cloud autosave (existing quotes only, Pro/admin only)
  useEffect(() => {
    if (!initialLoaded || restoreOffer) return;
    if (!id) return; // can't autosave a quote that hasn't been created yet
    if (!user || (!user.is_pro && user.role !== 'admin')) return;
    if (busy) return;
    if (!name?.trim()) return;
    const handle = setTimeout(async () => {
      setAutosaveState('saving');
      try {
        const itemsTotalLocal = items.reduce(
          (s, it) => s + (parseFloat(it.qty) || 0) * (parseFloat(it.unit_price) || 0), 0);
        const materialsCostLocal = itemsTotalLocal + (parseFloat(extraMaterials) || 0);
        await api.put(`/projects/${id}`, {
          name, trade,
          quote_number: quoteNumber || null,
          customer_name: customer.name || null,
          customer_address: customer.address || null,
          customer_phone: customer.phone || null,
          customer_email: customer.email || null,
          items,
          labour_hours: parseFloat(labourHours) || 0,
          labour_rate: parseFloat(labourRate) || 0,
          labour_days: parseFloat(labourDays) || 0,
          labour_day_rate: parseFloat(labourDayRate) || 0,
          materials_cost: materialsCostLocal,
          vat_rate: parseFloat(vat) || 0,
          margin_pct: parseFloat(margin) || 0,
          overhead_per_day: parseFloat(overheadsPerDay) || 0,
          notes,
          status,
          pdf_template: pdfTemplate,
          payment_terms: paymentTerms,
          terms,
          quote_validity_days: parseInt(quoteValidityDays) || 30,
        });
        setAutosaveState('saved');
        // localStorage no longer needed once cloud has the latest
        try { localStorage.removeItem(draftKey); } catch { /* ignore */ }
      } catch {
        // Auth refresh / network failure — keep localStorage draft as the safety net
        setAutosaveState('offline');
      }
    }, 2000);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formState, initialLoaded, id, user, restoreOffer]);

  const itemsTotal = useMemo(
    () => items.reduce((s, it) => s + (parseFloat(it.qty) || 0) * (parseFloat(it.unit_price) || 0), 0),
    [items]
  );
  const materialsCost = itemsTotal + (parseFloat(extraMaterials) || 0);
  const labourCost = (parseFloat(labourHours) || 0) * (parseFloat(labourRate) || 0)
                   + (parseFloat(labourDays) || 0) * (parseFloat(labourDayRate) || 0);
  const overheadsCost = (parseFloat(labourDays) || 0) * (parseFloat(overheadsPerDay) || 0);
  const subtotal = materialsCost + labourCost + overheadsCost;
  const marginAmt = subtotal * (parseFloat(margin) || 0) / 100;
  const afterMargin = subtotal + marginAmt;
  const vatAmt = afterMargin * (parseFloat(vat) || 0) / 100;
  const total = afterMargin + vatAmt;

  const updateItem = (i, key, val) => {
    setItems((arr) => arr.map((it, idx) => idx === i ? { ...it, [key]: val } : it));
  };

  const save = async () => {
    if (!user) { toast.error('Log in to save quotes'); nav('/login'); return; }
    if (!user.is_pro && user.role !== 'admin') {
      toast.error('Saving quotes is a Pro feature. Start your 14-day free trial.');
      nav('/pricing');
      return;
    }
    if (!name.trim()) { toast.error('Give your quote a name'); return; }
    setBusy(true);
    try {
      const payload = {
        name, trade,
        quote_number: quoteNumber || null,
        customer_name: customer.name || null,
        customer_address: customer.address || null,
        customer_phone: customer.phone || null,
        customer_email: customer.email || null,
        items,
        labour_hours: parseFloat(labourHours) || 0,
        labour_rate: parseFloat(labourRate) || 0,
        labour_days: parseFloat(labourDays) || 0,
        labour_day_rate: parseFloat(labourDayRate) || 0,
        materials_cost: materialsCost,
        vat_rate: parseFloat(vat) || 0,
        margin_pct: parseFloat(margin) || 0,
        overhead_per_day: parseFloat(overheadsPerDay) || 0,
        notes,
        status,
        pdf_template: pdfTemplate,
        payment_terms: paymentTerms,
        terms,
        quote_validity_days: parseInt(quoteValidityDays) || 30,
      };
      await (id
        ? api.put(`/projects/${id}`, payload)
        : api.post('/projects', payload));
      try { localStorage.removeItem(draftKey); } catch { /* ignore */ }
      toast.success(id ? 'Quote updated' : 'Quote saved');
      nav('/dashboard');
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || 'Save failed');
    } finally { setBusy(false); }
  };

  const createJobFromQuote = async () => {
    if (!(user?.is_pro || user?.role === 'admin')) { toast.error('The Job Tracker is a Pro feature. Start your 14-day free trial.'); return; }
    setCreatingJob(true);
    try {
      const { data } = await api.post('/jobs', { name: name || 'New job', quote_id: id });
      toast.success('Job created — tracking profit now');
      nav(`/jobs/${data.id}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || 'Could not create job'); }
    finally { setCreatingJob(false); }
  };

  const printPdf = () => {
    if (!user) { toast.error('Log in to export PDF'); nav('/login'); return; }
    if (!user.is_pro && user.role !== 'admin') {
      toast.error('Branded PDF export is a Pro feature. Start your 14-day free trial.');
      nav('/pricing');
      return;
    }
    window.print();
  };

  const downloadPdf = async () => {
    if (!user) { toast.error('Log in to export PDF'); nav('/login'); return; }
    if (!user.is_pro && user.role !== 'admin') {
      toast.error('Branded PDF export is a Pro feature. Start your 14-day free trial.');
      nav('/pricing');
      return;
    }
    const node = pdfRef.current?.querySelector('.print-page');
    if (!node) { toast.error('Nothing to export yet'); return; }
    setDownloadingPdf(true);
    try {
      const html2pdf = (await import('html2pdf.js')).default;
      const safe = (quoteNumber || name || 'quote').toString().replace(/[^a-z0-9-_]+/gi, '-');
      await html2pdf().set({
        margin: [10, 8, 10, 8],
        filename: `${safe}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true, backgroundColor: '#ffffff', windowWidth: 1024 },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
        pagebreak: { mode: ['css', 'legacy'] },
      }).from(node).save();
      toast.success('PDF downloaded');
    } catch (e) {
      toast.error('Could not generate PDF — try Print / PDF instead');
    } finally {
      setDownloadingPdf(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <Seo title="Quote Estimator" description="Build professional trade quotes — line items, labour, VAT and margin. Save, share and export branded PDFs." path={id ? null : '/estimator'} noindex={!!id} />
      <Toaster richColors position="top-right" />
      <header className="mb-8 flex items-start justify-between flex-wrap gap-3">
        <div>
          <p className="text-xs uppercase tracking-widest text-brand font-semibold">Estimator</p>
          <h1 className="font-display font-semibold text-4xl sm:text-5xl uppercase tracking-tight mt-1">Build a Quote</h1>
          <p className="text-slate-600 mt-2 max-w-2xl">Add line items, labour, VAT and margin. Save to your dashboard or print as PDF.</p>
          {(autosaveState === 'saving' || autosaveState === 'saved' || autosaveState === 'offline') && id && (
            <p
              data-testid="autosave-indicator"
              className={`font-mono text-[11px] uppercase tracking-widest mt-3 ${
                autosaveState === 'offline' ? 'text-rose-600' : autosaveState === 'saving' ? 'text-slate-500' : 'text-emerald-700'
              }`}
            >
              {autosaveState === 'saving' && 'Auto-saving…'}
              {autosaveState === 'saved' && 'Auto-saved'}
              {autosaveState === 'offline' && 'Offline — your changes are kept safely in this browser'}
            </p>
          )}
        </div>
        <div className="flex flex-col sm:flex-row gap-2 print:hidden">
          <div className="flex flex-col">
            <select
              data-testid="estimator-pdf-template"
              value={pdfTemplate}
              onChange={(e) => setPdfTemplate(e.target.value)}
              className="px-3 py-2 border border-slate-300 text-xs uppercase tracking-widest text-slate-700 bg-white hover:border-slate-200 cursor-pointer font-semibold"
              title="Pick which PDF template to print — Simple hides prices/labour rates from your customer."
            >
              <option value="detailed">PDF: Detailed (internal)</option>
              <option value="simple">PDF: Simple (customer)</option>
            </select>
            <p className="font-mono text-[10px] text-slate-500 mt-1 max-w-[220px] leading-tight">
              {pdfTemplate === 'simple'
                ? 'Customer view — prices & labour rates hidden.'
                : 'Internal view — full breakdown shown.'}
            </p>
          </div>
          <label className="flex items-center gap-2 px-3 py-2 border border-slate-300 cursor-pointer text-xs uppercase tracking-widest text-slate-700 hover:border-slate-200 font-semibold" title="Show the margin row on the PDF (only valid for Detailed template)">
            <input
              data-testid="estimator-show-margin-toggle"
              type="checkbox"
              checked={showMarginOnPdf}
              onChange={(e) => setShowMarginOnPdf(e.target.checked)}
              disabled={pdfTemplate === 'simple'}
              className="accent-brand"
            />
            Show margin
          </label>
          <button data-testid={EST.save} onClick={save} disabled={busy} className="tm-btn">
            <Save size={16}/> {busy ? (id ? 'Updating…' : 'Saving…') : (user?.is_pro || user?.role === 'admin' ? (id ? 'Update' : 'Save') : 'Save (Pro)')}
          </button>
          <button data-testid="estimator-preview-btn" onClick={() => setPreviewOpen(true)} className="tm-btn" title="See exactly what your customer will see">
            <Eye size={16}/> Preview
          </button>
          {id && (
            <button data-testid="estimator-create-job-btn" onClick={createJobFromQuote} disabled={creatingJob} className="tm-btn" title="Create a Job in the Job Tracker pre-filled from this quote">
              <Briefcase size={16}/> {creatingJob ? 'Creating…' : 'Create Job'}
            </button>
          )}
          <button data-testid="estimator-download-pdf-btn" onClick={downloadPdf} disabled={downloadingPdf} className="tm-btn" title="Download the quote as a PDF file">
            <Download size={16}/> {downloadingPdf ? 'Preparing…' : (user?.is_pro || user?.role === 'admin' ? 'Download PDF' : 'Download PDF (Pro)')}
          </button>
          <button data-testid={EST.exportPdf} onClick={printPdf} className="tm-btn tm-btn-dark">
            <Printer size={16}/> {user?.is_pro || user?.role === 'admin' ? 'Print / PDF' : 'Print / PDF (Pro)'}
          </button>
        </div>
      </header>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {(user?.is_pro || user?.role === 'admin') && (
            <QuoteTemplateBar
              current={{ paymentTerms, overheadsPerDay, labourHours, labourRate, labourDays, labourDayRate, extraMaterials, items, vat, margin, quoteValidityDays, pdfTemplate, notes, terms }}
              onApply={(t) => {
                if (Array.isArray(t.items) && t.items.length) setItems(t.items.map((it) => ({ ...blankItem(), ...it })));
                if (t.labour_hours != null) setLabourHours(t.labour_hours);
                if (t.labour_rate != null) setLabourRate(t.labour_rate);
                if (t.labour_days != null) setLabourDays(t.labour_days);
                if (t.labour_day_rate != null) setLabourDayRate(t.labour_day_rate);
                if (t.extra_materials != null) setExtraMaterials(t.extra_materials);
                if (t.payment_terms != null) setPaymentTerms(t.payment_terms);
                if (t.overhead_per_day != null) setOverheadsPerDay(t.overhead_per_day);
                if (t.vat_rate != null) setVat(t.vat_rate);
                if (t.margin_pct != null) setMargin(t.margin_pct);
                if (t.quote_validity_days != null) setQuoteValidityDays(t.quote_validity_days);
                if (t.pdf_template) setPdfTemplate(t.pdf_template);
                if (t.notes != null) setNotes(t.notes);
                if (t.terms != null) setTerms(t.terms);
              }}
            />
          )}
          {restoreOffer && (
            <div data-testid="restore-banner" className="tm-card p-5 border border-brand bg-brand/5 flex items-start gap-4 flex-wrap">
              <div className="flex-1 min-w-[260px]">
                <p className="font-display font-semibold uppercase tracking-tight text-lg">Unsaved changes found</p>
                <p className="text-sm text-slate-700 mt-1">
                  We found a draft of this quote saved in your browser from {new Date(restoreOffer.ts).toLocaleString('en-GB')}. Restore your work?
                </p>
              </div>
              <div className="flex gap-2">
                <button
                  data-testid="restore-discard-btn"
                  onClick={dismissRestore}
                  className="border border-slate-200 bg-white hover:bg-slate-100 px-4 py-2 text-xs uppercase tracking-widest font-semibold"
                >
                  Discard
                </button>
                <button
                  data-testid="restore-apply-btn"
                  onClick={applyRestore}
                  className="bg-brand text-white border border-slate-200 hover:bg-[#e84a14] px-4 py-2 text-xs uppercase tracking-widest font-semibold"
                >
                  Restore
                </button>
              </div>
            </div>
          )}
          <div className="tm-card p-6 space-y-4">
            <div className="grid sm:grid-cols-3 gap-4">
              <div className="sm:col-span-2">
                <label className="tm-label">Quote name</label>
                <input data-testid={EST.name} value={name} onChange={(e) => setName(e.target.value)} className="tm-input" placeholder="Bathroom refit – 12 Oak Ave"/>
              </div>
              <div>
                <label className="tm-label">Quote / Invoice no.</label>
                <input data-testid="est-quote-number" value={quoteNumber} onChange={(e) => setQuoteNumber(e.target.value)} className="tm-input" placeholder="TM-0001"/>
              </div>
              <div className="sm:col-span-2">
                <label className="tm-label">Status</label>
                <select data-testid="est-status" value={status} onChange={(e) => setStatus(e.target.value)} className="tm-input">
                  {STATUS_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </div>
            </div>
          </div>

          <div className="tm-card p-6 space-y-4">
            <div className="flex items-center gap-2">
              <User2 size={18} className="text-brand"/>
              <h3 className="font-display font-bold text-xl uppercase tracking-tight">Customer Details</h3>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="tm-label">Customer name</label>
                <input data-testid="est-customer-name" value={customer.name} onChange={(e) => setCustomer({...customer, name: e.target.value})} className="tm-input" placeholder="Mrs Jane Smith"/>
              </div>
              <div>
                <label className="tm-label">Phone</label>
                <input data-testid="est-customer-phone" value={customer.phone} onChange={(e) => setCustomer({...customer, phone: e.target.value})} className="tm-input" placeholder="07123 456789"/>
              </div>
              <div className="sm:col-span-2">
                <label className="tm-label">Address</label>
                <textarea data-testid="est-customer-address" value={customer.address} onChange={(e) => setCustomer({...customer, address: e.target.value})} className="tm-input" rows={2} placeholder="12 Oak Avenue, Anytown, AB1 2CD"/>
              </div>
              <div className="sm:col-span-2">
                <label className="tm-label">Email</label>
                <input data-testid="est-customer-email" type="email" value={customer.email} onChange={(e) => setCustomer({...customer, email: e.target.value})} className="tm-input" placeholder="jane@example.com"/>
              </div>
            </div>
            {(user?.is_pro || user?.role === 'admin') && !user?.business_name && (
              <p className="text-sm font-mono text-slate-500 bg-amber-50 border border-amber-300 p-2">
                💡 Tip: add your business name &amp; logo in{' '}
                <Link to="/settings" className="underline font-bold">Settings</Link>{' '}so they appear on printed invoices.
              </p>
            )}
          </div>

          <div className="tm-card p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display font-bold text-xl uppercase tracking-tight">Materials &amp; Line Items</h3>
              <button data-testid={EST.addItem} onClick={() => setItems((a) => [...a, blankItem()])} className="tm-btn text-xs min-h-0 py-2">
                <Plus size={14}/> Add line
              </button>
            </div>
            <div className="space-y-3">
              {items.map((it, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-end">
                  <div className="col-span-12 sm:col-span-5">
                    <label className="tm-label">Description</label>
                    <input data-testid={EST.itemDesc(i)} value={it.description} onChange={(e) => updateItem(i, 'description', e.target.value)} className="tm-input" placeholder="Sand &amp; cement, paint, tiles…"/>
                  </div>
                  <div className="col-span-4 sm:col-span-2">
                    <label className="tm-label">Qty</label>
                    <input data-testid={EST.itemQty(i)} type="number" step="any" value={it.qty} onChange={(e) => updateItem(i, 'qty', e.target.value)} className="tm-input"/>
                  </div>
                  <div className="col-span-4 sm:col-span-2">
                    <label className="tm-label">Unit</label>
                    <input value={it.unit} onChange={(e) => updateItem(i, 'unit', e.target.value)} className="tm-input" placeholder="m², bag"/>
                  </div>
                  <div className="col-span-3 sm:col-span-2">
                    <label className="tm-label">£/unit</label>
                    <input data-testid={EST.itemUnit(i)} type="number" step="any" value={it.unit_price} onChange={(e) => updateItem(i, 'unit_price', e.target.value)} className="tm-input"/>
                  </div>
                  <div className="col-span-1">
                    <button data-testid={EST.itemRm(i)} onClick={() => setItems((arr) => arr.filter((_, idx) => idx !== i))} className="border border-slate-200 bg-white hover:bg-red-50 p-3" aria-label="Remove">
                      <Trash2 size={16}/>
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-5 flex items-center justify-between bg-slate-50 border border-slate-200 p-3">
              <span className="text-xs uppercase tracking-widest font-semibold">Line items subtotal</span>
              <span className="font-mono font-bold text-xl">£{itemsTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
            </div>
          </div>

          <div className="tm-card p-6">
            <p className="font-display font-bold uppercase tracking-tight text-sm text-slate-600 mb-3">Labour — use hourly, daily, or both</p>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="tm-label">Labour hours</label>
                <input data-testid={EST.labourHours} type="number" step="any" value={labourHours} onChange={(e) => setLabourHours(e.target.value)} className="tm-input"/>
              </div>
              <div>
                <label className="tm-label">Labour rate £/hr</label>
                <input data-testid={EST.labourRate} type="number" step="any" value={labourRate} onChange={(e) => setLabourRate(e.target.value)} className="tm-input"/>
              </div>
              <div>
                <label className="tm-label">Labour days</label>
                <input data-testid="est-labour-days" type="number" step="any" value={labourDays} onChange={(e) => setLabourDays(e.target.value)} className="tm-input"/>
              </div>
              <div>
                <label className="tm-label">Day rate £/day</label>
                <input data-testid="est-labour-day-rate" type="number" step="any" value={labourDayRate} onChange={(e) => setLabourDayRate(e.target.value)} className="tm-input"/>
              </div>
            </div>
            <p className="font-mono text-xs text-slate-500 mt-3">Tip: leave hours at 0 if you bill by the day (or days at 0 if you bill by the hour). Use both if a job has a fixed day-rate crew plus extra evening hours.</p>
          </div>

          <div className="tm-card p-6 grid sm:grid-cols-2 gap-4">
            <div>
              <label className="tm-label">Other materials £</label>
              <input data-testid={EST.materials} type="number" step="any" value={extraMaterials} onChange={(e) => setExtraMaterials(e.target.value)} className="tm-input" placeholder="0"/>
            </div>
            <div>
              <label className="tm-label">VAT %</label>
              <input data-testid={EST.vat} type="number" step="any" value={vat} onChange={(e) => setVat(e.target.value)} className="tm-input"/>
            </div>
            <div>
              <label className="tm-label">Margin %</label>
              <input data-testid={EST.margin} type="number" step="any" value={margin} onChange={(e) => setMargin(e.target.value)} className="tm-input"/>
            </div>
            <div>
              <label className="tm-label">Overheads £ / day</label>
              <input
                data-testid="est-overheads-per-day"
                type="number"
                step="any"
                min="0"
                value={overheadsPerDay}
                onChange={(e) => setOverheadsPerDay(e.target.value)}
                className="tm-input"
                placeholder="0"
              />
              <p className="font-mono text-[10px] text-slate-500 mt-1 leading-tight">
                Multiplied by labour days. Not sure?{' '}
                <Link to="/trades/business-essentials" className="underline hover:text-brand">
                  Use the Day Rate Calculator
                </Link>{' '}
                to work out what you need to charge per day to cover your overheads.
              </p>
            </div>
          </div>

          <div className="tm-card p-6">
            <label className="tm-label">Notes</label>
            <textarea data-testid={EST.notes} value={notes} onChange={(e) => setNotes(e.target.value)} rows={4} className="tm-input" placeholder="Customer details, scope, exclusions…"/>
          </div>

          <div className="tm-card p-6 space-y-4">
            <div>
              <p className="font-display font-bold uppercase tracking-tight text-sm text-slate-600">PDF Footer — visible to customer</p>
              <p className="font-mono text-xs text-slate-500 mt-1">These appear at the bottom of the printed quote / PDF.</p>
            </div>
            <div className="grid sm:grid-cols-3 gap-4">
              <div className="sm:col-span-2">
                <label className="tm-label">Payment terms</label>
                <textarea
                  data-testid="est-payment-terms"
                  value={paymentTerms}
                  onChange={(e) => setPaymentTerms(e.target.value)}
                  rows={3}
                  className="tm-input"
                  placeholder="Payment terms: 14 days from invoice unless otherwise agreed."
                />
              </div>
              <div>
                <label className="tm-label">Quote valid for (days)</label>
                <input
                  data-testid="est-quote-validity-days"
                  type="number"
                  min="1"
                  step="1"
                  value={quoteValidityDays}
                  onChange={(e) => setQuoteValidityDays(e.target.value)}
                  className="tm-input"
                  placeholder="30"
                />
              </div>
            </div>
            <div>
              <label className="tm-label">Terms &amp; Conditions / Stipulations</label>
              <textarea
                data-testid="est-terms"
                value={terms}
                onChange={(e) => setTerms(e.target.value)}
                rows={5}
                className="tm-input"
                placeholder={'e.g.\n• 50% deposit required before work begins.\n• Prices exclude any unforeseen remedial works.\n• Guarantee: 12 months on labour.\n• Access and parking to be provided by the customer.'}
              />
              <p className="text-[10px] text-slate-400 uppercase tracking-widest mt-1 font-semibold">Shown to the customer on the quote and included on the PDF</p>
            </div>
          </div>

          {id && savedProject && (user?.is_pro || user?.role === 'admin') && (
            <PhotoAttachments key={savedProject.id || id} projectId={id} initialPhotos={savedProject.photos || []} />
          )}

          {id && (user?.is_pro || user?.role === 'admin') && (
            user?.is_pro_plus || user?.role === 'admin' ? (
              <ShareSection
                projectId={id}
                quote={savedProject}
                onUpdate={(updated) => setSavedProject(updated)}
              />
            ) : (
              <div className="tm-card p-6 bg-brand/5 border" style={{ borderColor: 'var(--tm-orange)' }}>
                <div className="flex items-center gap-2 mb-2">
                  <span className="tm-chip tm-chip-orange">PRO+</span>
                  <h3 className="font-display font-bold text-lg uppercase tracking-tight">Customer share link</h3>
                </div>
                <p className="text-sm text-slate-700">Send your customer a branded online quote. See exactly when they view, accept or decline — with email notifications.</p>
                <Link to="/pricing" data-testid="upgrade-to-pro-plus" className="tm-btn mt-4 inline-flex">Upgrade to Pro+ for £9.99/mo</Link>
              </div>
            )
          )}
        </div>

        <aside className="space-y-4 lg:sticky lg:top-24 self-start">
          <div className="tm-card tm-card-dark p-6 border border-slate-200 shadow-md">
            <p className="text-xs uppercase tracking-widest text-slate-400 font-semibold">Live Total</p>
            <p data-testid={EST.total} className="font-mono font-semibold text-5xl text-brand mt-1 tracking-tight">£{total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
            <hr className="my-4 border-slate-700"/>
            <Row k="Materials" v={materialsCost}/>
            <Row k="Labour" v={labourCost}/>
            {overheadsCost > 0 && <Row k="Overheads" v={overheadsCost}/>}
            <Row k="Subtotal" v={subtotal}/>
            <Row k={`Margin (${margin}%)`} v={marginAmt}/>
            <Row k={`VAT (${vat}%)`} v={vatAmt}/>
          </div>
          <p className="font-mono text-xs text-slate-500 leading-relaxed">Approximate — confirm prices on site. UK standard VAT is 20%.</p>
        </aside>
      </div>

      {/* Hidden invoice — used for printing and PDF download capture */}
      <div ref={pdfRef} className="pdf-capture-host" aria-hidden="true">
        <PrintableInvoice
          user={user} logoUrl={logoUrl}
          quoteNumber={quoteNumber} name={name} trade={trade} customer={customer}
          items={items} itemsTotal={itemsTotal}
          labourHours={parseFloat(labourHours) || 0}
          labourRate={parseFloat(labourRate) || 0}
          labourDays={parseFloat(labourDays) || 0}
          labourDayRate={parseFloat(labourDayRate) || 0}
          labourCost={labourCost}
          extraMaterials={parseFloat(extraMaterials) || 0}
          materialsCost={materialsCost} subtotal={subtotal}
          marginAmt={marginAmt} vatAmt={vatAmt} total={total}
          vat={vat} margin={margin} notes={notes}
          showMargin={showMarginOnPdf}
          template={pdfTemplate}
          paymentTerms={paymentTerms}
          quoteValidityDays={parseInt(quoteValidityDays) || 30}
          terms={terms}
        />
      </div>

      {previewOpen && (
        <div
          data-testid="pdf-preview-modal"
          className="fixed inset-0 z-[100] bg-slate-900/80 backdrop-blur-sm flex items-start justify-center overflow-y-auto p-4 sm:p-8"
          onClick={(e) => { if (e.target === e.currentTarget) setPreviewOpen(false); }}
        >
          <div className="bg-white shadow-md border border-slate-200 w-full max-w-[860px] my-auto">
            <div className="flex items-center justify-between border-b border-slate-200 px-5 py-3 bg-slate-50 sticky top-0 z-10">
              <div>
                <p className="text-[10px] uppercase tracking-widest text-brand font-semibold">Customer preview</p>
                <p className="font-display font-semibold uppercase tracking-tight text-lg">
                  {pdfTemplate === 'simple' ? 'Simple template (customer view)' : 'Detailed template (internal)'}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  data-testid="pdf-preview-download-btn"
                  onClick={downloadPdf}
                  disabled={downloadingPdf}
                  className="tm-btn text-xs min-h-0 py-2 px-3"
                  title="Download this quote as a PDF file"
                >
                  <Download size={14}/> {downloadingPdf ? 'Preparing…' : 'Download'}
                </button>
                <button
                  data-testid="pdf-preview-print-btn"
                  onClick={() => { setPreviewOpen(false); setTimeout(printPdf, 100); }}
                  className="tm-btn tm-btn-dark text-xs min-h-0 py-2 px-3"
                  title="Close preview and open the print dialog"
                >
                  <Printer size={14}/> Print this
                </button>
                <button
                  data-testid="pdf-preview-close-btn"
                  onClick={() => setPreviewOpen(false)}
                  className="border border-slate-200 bg-white hover:bg-slate-100 p-2"
                  aria-label="Close preview"
                >
                  <X size={16}/>
                </button>
              </div>
            </div>
            <div className="pdf-preview-wrap">
              <PrintableInvoice
                user={user} logoUrl={logoUrl}
                quoteNumber={quoteNumber} name={name} trade={trade} customer={customer}
                items={items} itemsTotal={itemsTotal}
                labourHours={parseFloat(labourHours) || 0}
                labourRate={parseFloat(labourRate) || 0}
                labourDays={parseFloat(labourDays) || 0}
                labourDayRate={parseFloat(labourDayRate) || 0}
                labourCost={labourCost}
                extraMaterials={parseFloat(extraMaterials) || 0}
                materialsCost={materialsCost} subtotal={subtotal}
                marginAmt={marginAmt} vatAmt={vatAmt} total={total}
                vat={vat} margin={margin} notes={notes}
                showMargin={showMarginOnPdf}
                template={pdfTemplate}
                paymentTerms={paymentTerms}
                quoteValidityDays={parseInt(quoteValidityDays) || 30}
                terms={terms}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex items-baseline justify-between py-1 font-mono text-sm">
      <span className="text-slate-300 uppercase text-xs tracking-widest">{k}</span>
      <span className="font-bold">£{(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
    </div>
  );
}
